import styles from './TableCardView.module.scss';
import { Empty, Spin } from 'antd';
import React from 'react';
import { TableCardViewCard } from './TableCardViewCard';
import { TableCardViewControls } from './TableCardViewControls';
import { useTableCardView } from './useTableCardView';
import { TableColumnType } from '../Table';

export interface TableCardViewProps<T> {
  columns: TableColumnType<T>[];
  dataSource: readonly T[] | undefined;
  rowKey: (record: T, index: number) => React.Key;
  loading?: boolean;
  rowClassName?: string;
  columnsControl?: React.ReactNode;
}

/**
 * Mobile / tablet representation of a table: renders the rows as cards with a
 * dedicated panel for sorting and filtering. Sorting and filtering reuse the
 * column's own `sorter` / `onFilter` definitions so the behaviour matches the
 * desktop table exactly.
 */
export function TableCardView<T extends object>(props: TableCardViewProps<T>) {
  const { columns, dataSource, rowKey, loading, rowClassName, columnsControl } =
    props;

  const {
    sortableColumns,
    filterableColumns,
    sort,
    setSort,
    filters,
    setFilters,
    displayData,
  } = useTableCardView(columns, dataSource);

  if (loading) {
    return <Spin />;
  }

  return (
    <div className={styles.container}>
      <TableCardViewControls
        columnsControl={columnsControl}
        sortableColumns={sortableColumns}
        filterableColumns={filterableColumns}
        sort={sort}
        onSortChange={setSort}
        filters={filters}
        onFiltersChange={setFilters}
      />
      <div className={styles.list}>
        {displayData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          displayData.map((record, index) => (
            <TableCardViewCard
              key={rowKey(record, index)}
              columns={columns}
              record={record}
              index={index}
              className={rowClassName}
            />
          ))
        )}
      </div>
    </div>
  );
}
